"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isOpen, setIsOpen] = useState(false);
  if (loading) {
    return <div data-section-id="223699" className="flex gap-6 items-center">
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <>
      <div className="hidden md:block">
        <NavigationMenu viewport={false}>
          <NavigationMenuList className="gap-10">
            {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
                <NavigationMenuLink asChild>
                  <Link to={item.href || "#"} className="group relative font-default font-medium tracking-wide uppercase text-xs text-foreground/80 hover:text-foreground transition-colors py-2 flex flex-col items-center">
                    {item.label}
                    <span className="absolute -bottom-2 w-full h-[2px] rounded-lg bg-secondary opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>)}
          </NavigationMenuList>
        </NavigationMenu>
      </div>

      <Button variant="ghost" size="icon" className="md:hidden text-foreground p-2" onClick={() => setIsOpen(!isOpen)} aria-label="Toggle Menu">
        {isOpen ? <X size={24} strokeWidth={1.5} /> : <Menu size={24} strokeWidth={1.5} />}
      </Button>

      {isOpen && <div className="absolute top-full left-0 w-full bg-background/95 backdrop-blur-md border-b border-border/10 flex flex-col items-center py-8 gap-8 md:hidden shadow-2xl z-50">
          {menuItems.map((item, i) => <div key={item.id} data-index={i}>
              <Link to={item.href || "#"} className="group relative font-default font-medium tracking-wide uppercase text-sm text-foreground/80 hover:text-foreground transition-colors flex flex-col items-center" onClick={() => setIsOpen(false)}>
                {item.label}
                <span className="absolute -bottom-2 w-full h-[2px] rounded-lg bg-secondary opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              </Link>
            </div>)}
        </div>}
    </>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className={`sticky top-0 w-full z-[100] transition-all duration-500 bg-background text-foreground ${scrolled ? "bg-background/80 backdrop-blur-[12px] border-b border-border/10 shadow-sm" : ""}`}>
      <div className="container mx-auto px-6 lg:px-12 h-24 flex items-center justify-between">
        <div className="flex-shrink-0 w-40">
          <Link to="/" className="block">
            <WvcLogo className="h-7 w-auto" />
          </Link>
        </div>

        <MenuProvider menu_id="42" className="flex-1 flex justify-center">
          <HeaderMenuConsumer />
        </MenuProvider>

        <div className="hidden md:flex flex-shrink-0 w-40 justify-end" />
      </div>
    </section>;
}